import Quill from 'quill';
import React, { forwardRef, useEffect, useRef, useLayoutEffect } from 'react';

// Editor is a controlled React component
const QuillHtmlEditor = forwardRef(
  ({ readOnly, defaultValue, onTextChange, onSelectionChange, placeholder }: {
    readOnly?: boolean;
    defaultValue?: any;
    onTextChange?: (delta: any, oldContents: any, source: any) => void;
    onSelectionChange?: (range: any, oldRange: any, source: any) => void;
    placeholder?: string;
  }, ref: React.Ref<any>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const isInitializedRef = useRef(false);
    const lastContentRef = useRef<string>('');

    // Update refs when props change
    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
    });

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // Ensure container is completely empty
      container.innerHTML = '';

      // Create a single editor div
      const editorDiv = document.createElement('div');
      editorDiv.id = 'quill-editor-' + Math.random().toString(36).substr(2, 9);
      container.appendChild(editorDiv);

      // Initialize Quill on the editor div
      const quill = new Quill(editorDiv, {
        theme: 'snow',
        placeholder: placeholder || 'Enter description...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
          ]
        }
      });

      quillRef.current = quill;

      // Set up ref
      if (ref && 'current' in ref) {
        ref.current = quill;
      }

      // Set initial content only once
      if (defaultValueRef.current && !isInitializedRef.current) {
        try {
          quill.setContents(defaultValueRef.current);
          isInitializedRef.current = true;
          lastContentRef.current = JSON.stringify(quill.getContents());
        } catch (error) {
          console.warn('Failed to set initial content:', error);
        }
      }

      // Set up event listeners
      quill.on('text-change', (...args) => {
        onTextChangeRef.current?.(...args);
      });

      quill.on('selection-change', (...args) => {
        onSelectionChangeRef.current?.(...args);
      });

      // Cleanup function
      return () => {
        if (quillRef.current) {
          quillRef.current = null;
        }
        if (ref && 'current' in ref) {
          ref.current = null;
        }
        container.innerHTML = '';
        isInitializedRef.current = false;
      };
    }, []); // Only run once on mount

    // Handle readOnly changes
    useEffect(() => {
      if (quillRef.current) {
        quillRef.current.enable(!readOnly);
      }
    }, [readOnly]);

    // Handle defaultValue changes - only update if content is actually different
    useEffect(() => {
      if (!quillRef.current || !defaultValue || !isInitializedRef.current) {
        defaultValueRef.current = defaultValue;
        return;
      }

      try {
        // Handle both Delta objects and JSON strings
        let delta = defaultValue;
        if (typeof defaultValue === 'string') {
          try {
            delta = JSON.parse(defaultValue);
          } catch (parseError) {
            console.warn('Failed to parse defaultValue as JSON:', parseError);
            return;
          }
        }

        // Convert to string for comparison
        const newContentString = JSON.stringify(delta);
        
        // Only update if content is actually different
        if (newContentString !== lastContentRef.current) {
          // Store current cursor position
          const currentSelection = quillRef.current.getSelection();
          
          // Update content
          quillRef.current.setContents(delta);
          
          // Update last content reference
          lastContentRef.current = newContentString;
          
          // Restore cursor position if it was valid
          if (currentSelection && currentSelection.index !== null) {
            // Adjust cursor position if needed
            const newLength = quillRef.current.getLength();
            const adjustedIndex = Math.min(currentSelection.index, newLength - 1);
            quillRef.current.setSelection(adjustedIndex, currentSelection.length || 0);
          }
        }
      } catch (error) {
        console.warn('Failed to update content:', error);
      }
      
      defaultValueRef.current = defaultValue;
    }, [defaultValue]);

    return <div ref={containerRef} className="quill-editor-container"></div>;
  },
);

QuillHtmlEditor.displayName = 'QuillHtmlEditor';

export default QuillHtmlEditor;